import React, { useMemo } from "react";
import _ from "lodash";
import { FilterSelect } from "./Filter";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { selectProvidersDoctors } from "../../store/selector";
import { makeStyleWrapper } from "../../utils/styles";
import { BLACK, GRAY_600, WHITE } from "../../constant/colors";
import { fontWeights } from "../../constant/styles";
import styled from "styled-components";
import "../../locales/i18n";

import { Space, Typography, Tooltip } from "antd";
import { buildOneLineAddress } from "../../utils/parse";
import { filterSort } from "../../utils/sort";

import { getNamesFromFullName } from "../Patient/ApplicationFormEditor/utils";

const { Text } = Typography;
const { Option } = FilterSelect;

// Dropdown and subtext width configuration
const DROPDOWN_WIDTH = 420;
const SUBTEXT_MAX_WIDTH = 380;
// Approximate character threshold for showing tooltip (based on avg char width at current font size)
const TOOLTIP_CHAR_THRESHOLD = 50;

const optionWrapper = makeStyleWrapper(`
  font-weight: normal;
  .title {
    font-weight: ${fontWeights.semibold};
  }
  .subtext {
    color: ${GRAY_600};
  }
`);

// Hide the address in selection box
const StyledSelect = styled(FilterSelect)`
  .subtext {
    display: none;
  }
`;

export function PhysicianSelect({ placeholder, value, fixedNameOrder, inactivePhysician, ...props }) {
  const { t } = useTranslation();
  const physicians = useSelector(selectProvidersDoctors);
  const mergedPlaceholder = placeholder || t("no_physician");
  const valueBy = props?.valueBy || "id";

  const uniquePhysicians = _.uniqBy(physicians, (physician) =>
    [
      physician.name,
      physician.address,
      physician.suiteNumber,
      physician.city,
      physician.state,
      physician.zipCode,
      physician.npi,
      physician.phone,
      physician.fax,
      physician.email
    ].join("-")
  );

  // Helper to format physician display with subtext
  const formatPhysicianOption = (provider, isForLabel = false) => {
    let formattedName = provider.name;
    if (fixedNameOrder) {
      const { firstName, middleName, lastName } = getNamesFromFullName(provider.name);
      formattedName = [firstName, middleName, lastName].filter(Boolean).join(" ");
    }
    const subtext = [
      provider.npi && `NPI: ${provider.npi}`,
      buildOneLineAddress(provider),
      provider.phone && `Phone: ${provider.phone}`,
      provider.fax && `Fax: ${provider.fax}`,
      provider.email && `Email: ${provider.email}`
    ]
      .filter(Boolean)
      .join(", ");

    if (isForLabel) {
      return (
        <Space direction="vertical" size={0}>
          <Text className="title">{formattedName}</Text>
          <Text className="subtext">{subtext}</Text>
        </Space>
      );
    }
    return { formattedName, subtext };
  };

  const options = useMemo(
    () =>
      uniquePhysicians?.map((provider) => {
        const { formattedName, subtext } = formatPhysicianOption(provider);

        const needsTooltip = subtext.length > TOOLTIP_CHAR_THRESHOLD;

        const subtextElement = (
          <Text className="subtext" ellipsis style={{ maxWidth: SUBTEXT_MAX_WIDTH }}>
            {subtext}
          </Text>
        );

        return (
          <Option
            key={provider.id}
            label={formattedName}
            value={provider[valueBy]}
            title={formattedName}
            localSortKey={formattedName?.trim() || ""}
          >
            <Space direction="vertical" size={0}>
              <Text className="title">{formattedName}</Text>
              {needsTooltip ? (
                <Tooltip
                  title={<Text style={{ color: BLACK }}>{subtext}</Text>}
                  color={WHITE}
                  overlayInnerStyle={{ userSelect: "text", cursor: "text" }}
                >
                  {subtextElement}
                </Tooltip>
              ) : (
                subtextElement
              )}
            </Space>
          </Option>
        );
      }),
    [physicians, fixedNameOrder]
  );

  // If the caller provides an inactive physician, use labelInValue to display the name with details
  const displayValue = inactivePhysician ? { value, label: formatPhysicianOption(inactivePhysician, true) } : value;

  return (
    <StyledSelect
      filterSort={filterSort}
      filterOption
      optionFilterProp="label"
      dropdownMatchSelectWidth={DROPDOWN_WIDTH}
      dropdownRender={optionWrapper}
      // Do a loading state until we have the physicians list, otherwise
      // it renders as just an id until then. Blech!
      placeholder={physicians ? mergedPlaceholder : t("loading")}
      value={physicians ? displayValue : null}
      labelInValue={!!inactivePhysician}
      {...props}
    >
      {options}
    </StyledSelect>
  );
}
